import { LoginRequestSchema } from '@baseline/shared';
import { useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';

import { ApiError } from '../api/client.js';
import { useSession } from '../api/session.js';
import { Logo } from '../components/icons.js';

import type { SsoProvider } from '@baseline/shared';
import type { FormEvent } from 'react';

// Prototype login (plan Phase 11). The validation messages come from the shared
// LoginRequestSchema, which carries the prototype's copy.

function MicrosoftIcon() {
  return (
    <svg className="sso-ic" width="26" height="26" viewBox="0 0 24 24" aria-hidden="true">
      <rect x="2" y="2" width="9.5" height="9.5" rx="1" fill="#0F766E" />
      <rect x="12.5" y="2" width="9.5" height="9.5" rx="1" fill="#0D9488" />
      <rect x="2" y="12.5" width="9.5" height="9.5" rx="1" fill="#2DD4BF" />
      <rect x="12.5" y="12.5" width="9.5" height="9.5" rx="1" fill="#99F6E4" />
    </svg>
  );
}

function GoogleIcon() {
  return (
    <svg className="sso-ic" width="26" height="26" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M20.00 12.00A8 8 0 0 1 12.00 20.00" stroke="#2DD4BF" strokeWidth="3.6" fill="none" />
      <path d="M12.00 20.00A8 8 0 0 1 4.00 12.00" stroke="#0D9488" strokeWidth="3.6" fill="none" />
      <path d="M4.00 12.00A8 8 0 0 1 12.00 4.00" stroke="#0F766E" strokeWidth="3.6" fill="none" />
      <path d="M12.00 4.00A8 8 0 0 1 18.55 7.41" stroke="#99F6E4" strokeWidth="3.6" fill="none" />
      <rect x="12" y="10.2" width="8.4" height="3.6" fill="#2DD4BF" />
    </svg>
  );
}

const OKTA_RAYS: [string, string][] = [
  ['M12.00 6.80L12.00 1.60', '#0F766E'],
  ['M13.99 7.20L15.98 2.39', '#0F766E'],
  ['M15.68 8.32L19.35 4.65', '#0F766E'],
  ['M16.80 10.01L21.61 8.02', '#0F766E'],
  ['M17.20 12.00L22.40 12.00', '#0D9488'],
  ['M16.80 13.99L21.61 15.98', '#0D9488'],
  ['M15.68 15.68L19.35 19.35', '#0D9488'],
  ['M13.99 16.80L15.98 21.61', '#0D9488'],
  ['M12.00 17.20L12.00 22.40', '#2DD4BF'],
  ['M10.01 16.80L8.02 21.61', '#2DD4BF'],
  ['M8.32 15.68L4.65 19.35', '#2DD4BF'],
  ['M7.20 13.99L2.39 15.98', '#2DD4BF'],
  ['M6.80 12.00L1.60 12.00', '#99F6E4'],
  ['M7.20 10.01L2.39 8.02', '#99F6E4'],
  ['M8.32 8.32L4.65 4.65', '#99F6E4'],
  ['M10.01 7.20L8.02 2.39', '#99F6E4'],
];

function OktaIcon() {
  return (
    <svg
      className="sso-ic"
      width="26"
      height="26"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      {OKTA_RAYS.map(([d, stroke]) => (
        <path key={d} d={d} stroke={stroke} strokeWidth="2" strokeLinecap="round" />
      ))}
    </svg>
  );
}

export default function LoginPage() {
  const { state, login, sso } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [emailErr, setEmailErr] = useState('');
  const [pwErr, setPwErr] = useState('');
  const [busy, setBusy] = useState<'form' | 'sso' | null>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const pwRef = useRef<HTMLInputElement>(null);

  const from = (location.state as { from?: string } | null)?.from ?? '/';
  if (state.status === 'in' && busy === null) return <Navigate to={from} replace />;

  const done = () => void navigate(from, { replace: true });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const parsed = LoginRequestSchema.safeParse({ email, password });
    const issues = parsed.success ? [] : parsed.error.issues;
    const eErr = issues.find((i) => i.path[0] === 'email')?.message ?? '';
    const pErr = issues.find((i) => i.path[0] === 'password')?.message ?? '';
    setEmailErr(eErr);
    setPwErr(pErr);
    if (eErr || pErr) {
      (eErr ? emailRef : pwRef).current?.focus();
      return;
    }
    setBusy('form');
    try {
      await login(email.trim(), password);
      done();
    } catch (err) {
      setPwErr(err instanceof ApiError ? err.message : 'Baseline couldn’t sign you in. Try again.');
      setBusy(null);
    }
  };

  const signInWith = async (provider: SsoProvider) => {
    setBusy('sso');
    try {
      await sso(provider);
      done();
    } catch (err) {
      setPwErr(err instanceof ApiError ? err.message : 'Baseline couldn’t sign you in. Try again.');
      setBusy(null);
    }
  };

  return (
    <div className="login" id="login">
      <div className="lcard">
        <div className="lbrand lb-c">
          <Logo size={34} variant="dark" />
          Baseline
        </div>
        <form className="lc" id="login-form" noValidate onSubmit={(e) => void submit(e)}>
          <h1>Sign in to Baseline</h1>
          <p className="sub">Use your work account.</p>
          <div className="field">
            <label htmlFor="email">Work email</label>
            <input
              ref={emailRef}
              className="input"
              id="email"
              type="email"
              autoComplete="username"
              placeholder="name@company.com"
              aria-describedby="email-err"
              aria-invalid={emailErr ? true : undefined}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <div className="err" id="email-err" role="alert">
              {emailErr}
            </div>
          </div>
          <div className="field">
            <label htmlFor="password">Password</label>
            <div className="input-wrap">
              <input
                ref={pwRef}
                className="input"
                id="password"
                type={showPw ? 'text' : 'password'}
                autoComplete="current-password"
                aria-describedby="pw-err"
                aria-invalid={pwErr ? true : undefined}
                style={{ paddingRight: 64 }}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                className="show-pw"
                id="show-pw"
                type="button"
                aria-label={showPw ? 'Hide password' : 'Show password'}
                onClick={() => setShowPw((s) => !s)}
              >
                {showPw ? 'Hide' : 'Show'}
              </button>
            </div>
            <div className="err" id="pw-err" role="alert">
              {pwErr}
            </div>
          </div>
          <button
            className="btn btn-p login-btn"
            id="login-btn"
            type="submit"
            disabled={busy !== null}
          >
            {busy === 'form' ? 'Signing in…' : 'Sign in'}
          </button>
          <div className="or" aria-hidden="true">
            <span>or continue with</span>
          </div>
          <div className="sso-row">
            <button
              className="sso-b"
              id="sso-btn"
              type="button"
              title="Continue with Microsoft"
              aria-label="Continue with Microsoft SSO"
              disabled={busy !== null}
              onClick={() => void signInWith('microsoft')}
            >
              <MicrosoftIcon />
              <span id="sso-label" className="sr-only" aria-live="polite">
                {busy === 'sso' ? 'Redirecting to your identity provider…' : 'Log in with SSO'}
              </span>
            </button>
            <button
              className="sso-b"
              id="sso-google"
              type="button"
              title="Continue with Google"
              aria-label="Continue with Google SSO"
              disabled={busy !== null}
              onClick={() => void signInWith('google')}
            >
              <GoogleIcon />
            </button>
            <button
              className="sso-b"
              id="sso-okta"
              type="button"
              title="Continue with Okta"
              aria-label="Continue with Okta SSO"
              disabled={busy !== null}
              onClick={() => void signInWith('okta')}
            >
              <OktaIcon />
            </button>
          </div>
          <div className="demo-note">Demo build: enter any email and any password, or use SSO.</div>
        </form>
      </div>
      <div className="lnote">Baseline reads PDM and ERP. It never writes to either.</div>
    </div>
  );
}
